import React from 'react';
import { useTranslation } from 'react-i18next';

import { GaugeNumber, Icon, Reveal, Skeleton } from '../../../../components';
import { useCountUp } from '../../../../hooks/useCountUp';
import { ANIM, HOME, gaugeGradeColor, gaugeGradeGlow, type HomeGaugeId } from '../../../../theme/tokens';
import { Card, Ring, Center, Val, Status, Label, Foot, Chev } from './Gauge.styles';

// Source gauge geometry (viewBox 148): centre 74, radius 69.56, stroke 8.88. The
// SVG fills the responsive Ring box via this fixed viewBox, so only the box size
// changes between desktop and mobile.
const VIEWBOX = 148;
const CENTER = 74;
const RADIUS = 69.56;
const STROKE = 8.88;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export type GaugeProps = {
  /** Identifies the area for drill-in routing; the ring/glow hue now grades by
   *  `value` (shared gaugeGradeColor / gaugeGradeGlow), not by id. */
  id: HomeGaugeId;
  value: number;
  label: string;
  status: string;
  /** Stagger index → entrance delay (the gauges fill left→right). */
  delay?: number;
  /** When set, the whole card is a button into that area. */
  onOpen?: () => void;
  /** While true, the ring shows a placeholder (the score endpoint is loading). */
  loading?: boolean;
};

/**
 * The ring's animated content: the arc fills via `useCountUp` while the centred
 * number rolls up from 0 with the same NumberFlow odometer animation the Stats
 * dashboard uses (see `GaugeNumber`). Kept private and mounted only once the score
 * is in, so both entrance animations play on reveal instead of running unseen
 * behind a skeleton.
 */
const GaugeRing = ({
  value,
  statusLabel,
  color,
  delay,
}: {
  value: number;
  statusLabel: string;
  color: string;
  delay: number;
}): React.ReactElement => {
  const display = useCountUp(value, ANIM.HOME_GAUGE_MS, delay);
  const frac = Math.max(0, Math.min(1, display / 100));
  const dash = frac * CIRCUMFERENCE;

  return (
    <React.Fragment>
      <svg viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} aria-hidden="true">
        {/* full-circle track = the hue at 15% */}
        <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" stroke={color} strokeOpacity={0.15} strokeWidth={STROKE} />
        {/* progress arc = flat hue, round caps, starting at 12 o'clock */}
        <circle
          cx={CENTER}
          cy={CENTER}
          r={RADIUS}
          fill="none"
          stroke={color}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${CIRCUMFERENCE}`}
          transform={`rotate(-90 ${CENTER} ${CENTER})`}
        />
      </svg>
      <Center>
        <Val>
          <GaugeNumber value={value} animate delay={delay} />
        </Val>
        <Status text={statusLabel} tag="span" $color={color} />
      </Center>
    </React.Fragment>
  );
};

/**
 * A "Business Optimization Score" gauge — a coloured ring that fills on mount
 * while its number counts up in lock-step, a status band, the area label and a
 * drill-in chevron. The number and arc both derive from the same eased value so
 * they animate together (a faithful port of the source `Gauge`). The card chrome
 * (label, chevron) is static; only the ring loads in.
 */
export const Gauge = ({ id, value, label, status, delay = 0, onOpen, loading = false }: GaugeProps): React.ReactElement => {
  const { t } = useTranslation();
  // Ring/number/status colour grades by the score — the same logic the
  // Acquisition gauge uses — not a fixed per-area hue.
  const color = gaugeGradeColor(value);
  const clickable = !!onOpen;

  // `status` (gaugeStatusLabel → Optimal / Sustainable / Poor) and `label`
  // (Product / Acquisition / Operations) are categorical chrome sourced from
  // theme/mock-data as stable English ids; translate them here at the render site
  // so they localize (and re-evaluate on a language switch) without touching those
  // shared sources. Each branch wraps an explicit literal so the extractor harvests
  // every value; the English id stays the fallback (an unmapped value renders raw).
  const statusLabel = ((): string => {
    switch (status) {
      case 'Optimal':
        return t('Optimal');
      case 'Sustainable':
        return t('Sustainable');
      case 'Poor':
        return t('Poor');
      default:
        return status;
    }
  })();
  const areaLabel = ((): string => {
    switch (label) {
      case 'Product':
        return t('Product');
      case 'Acquisition':
        return t('Acquisition');
      case 'Operations':
        return t('Operations');
      default:
        return label;
    }
  })();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (onOpen && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <Card
      glow={gaugeGradeGlow(value)}
      interactive={clickable}
      onClick={onOpen}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? t('Open {{name}}', { name: areaLabel }) : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
    >
      <Ring>
        <Reveal overlay loading={loading} placeholder={<Skeleton circle block width="100%" height="100%" />}>
          <GaugeRing value={value} statusLabel={statusLabel} color={color} delay={delay} />
        </Reveal>
      </Ring>
      <Label text={areaLabel} tag="span" />
      <Foot>
        <Chev aria-hidden="true">
          <Icon name="arrow-right-circle" size={24} color={HOME.t50} />
        </Chev>
      </Foot>
    </Card>
  );
};

export default Gauge;
